/*
 * Complementary products (Kt) — for snippets/kt-complementary-products.liquid and snippets/kt-
 * complementary-product-card.liquid.
 */

(function () {
  if (!window.customElements) return;

  class KtVariantSelect extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.trigger = this.querySelector('.kt-cp-select__trigger');
      this.list = this.querySelector('.kt-cp-select__list');
      this.current = this.querySelector('.kt-cp-select__current');
      if (!this.trigger || !this.list || !this.current) return;

      this.options = Array.from(this.list.querySelectorAll('.kt-cp-select__option'));
      this.variantInput = this.closest('form') ? this.closest('form').querySelector('.product-variant-id') : null;
      if (!this.options.length || !this.variantInput) return;

      const card = this.closest('.kt-cp-card');
      this.price = card ? card.querySelector('.kt-cp-card__price .price') : null;

      this.initialised = true;

      this.options.forEach((option, index) => {
        option.id = this.list.id + '-option-' + index;
      });

      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.position = this.position.bind(this);

      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
      this.list.addEventListener('keydown', (event) => this.onListKeydown(event));
      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(option));
      });
    }

    disconnectedCallback() {
      this.releaseWhileOpenListeners();
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    get selectedOption() {
      return this.options.find((option) => option.getAttribute('aria-selected') === 'true') || this.options[0];
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.setAttribute('open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.list.hidden = false;
      this.position();
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      window.addEventListener('scroll', this.position, true);
      window.addEventListener('resize', this.position);
      this.highlight(this.selectedOption);
      this.list.focus();
    }

    close(returnFocus) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.list.hidden = true;
      this.releaseWhileOpenListeners();
      if (returnFocus) this.trigger.focus();
    }

    releaseWhileOpenListeners() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      window.removeEventListener('scroll', this.position, true);
      window.removeEventListener('resize', this.position);
    }

    position() {
      const trigger = this.trigger.getBoundingClientRect();

      this.list.style.width = trigger.width + 'px';

      this.list.style.left = '0px';
      this.list.style.top = '0px';
      const origin = this.list.getBoundingClientRect();

      this.list.style.left = trigger.left - origin.left + 'px';

      const spaceBelow = window.innerHeight - trigger.bottom;
      const listHeight = this.list.offsetHeight;
      const flipUp = spaceBelow < listHeight && trigger.top > spaceBelow;

      const top = flipUp ? trigger.top - listHeight + 1 : trigger.bottom - 1;
      this.list.style.top = top - origin.top + 'px';
    }

    onDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    highlight(option) {
      if (!option) return;
      this.options.forEach((item) => item.classList.toggle('is-active', item === option));
      this.list.setAttribute('aria-activedescendant', option.id);
      if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
      this.highlighted = option;
    }

    move(step) {
      const from = this.highlighted || this.selectedOption;
      const next = this.options[this.options.indexOf(from) + step];
      if (next) this.highlight(next);
    }

    onTriggerKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.open();
      }
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.move(-1);
          break;
        case 'Home':
          event.preventDefault();
          this.highlight(this.options[0]);
          break;
        case 'End':
          event.preventDefault();
          this.highlight(this.options[this.options.length - 1]);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.select(this.highlighted);
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'Tab':
          this.close();
          break;
        default:
          break;
      }
    }

    select(option) {
      if (!option) return;
      if (option.getAttribute('aria-disabled') === 'true') return;

      this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
      this.current.textContent = option.dataset.variantTitle || option.textContent.trim();
      this.variantInput.value = option.dataset.variantId;
      this.updatePrice(option);
      this.close(true);
    }

    updatePrice(option) {
      if (!this.price) return;

      const price = option.dataset.variantPrice;
      const compareAt = option.dataset.variantCompareAtPrice;
      if (!price) return;

      const onSale = Boolean(compareAt);
      this.price.classList.toggle('price--on-sale', onSale);

      this.setPriceText('.price__regular .price-item--regular', price);
      this.setPriceText('.price__sale .price-item--sale', price);
      if (onSale) this.setPriceText('.price__sale s.price-item--regular', compareAt);
    }

    setPriceText(selector, value) {
      const target = this.price.querySelector(selector);
      if (target) target.textContent = value;
    }
  }

  if (!customElements.get('kt-variant-select')) {
    customElements.define('kt-variant-select', KtVariantSelect);
  }

  class KtSliderDots extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      const sliderComponent = this.closest('slider-component');
      this.slider = sliderComponent ? sliderComponent.querySelector('[id^="Slider-"]') : null;
      if (!this.slider) return;

      this.initialised = true;
      this.currentPage = 1;

      this.render = this.render.bind(this);
      this.syncActive = this.syncActive.bind(this);

      this.slider.addEventListener('scroll', this.syncActive);
      this.resizeObserver = new ResizeObserver(this.render);
      this.resizeObserver.observe(this.slider);
    }

    disconnectedCallback() {
      if (this.slider) this.slider.removeEventListener('scroll', this.syncActive);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    measure() {
      const slides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter(
        (slide) => slide.clientWidth > 0
      );
      if (slides.length < 2) return null;

      const slideOffset = slides[1].offsetLeft - slides[0].offsetLeft;
      if (!slideOffset) return null;

      const perPage = Math.floor((this.slider.clientWidth - slides[0].offsetLeft) / slideOffset);
      const totalPages = Math.max(slides.length - Math.max(perPage, 1) + 1, 1);
      return { slideOffset: slideOffset, totalPages: totalPages };
    }

    render() {
      const measurements = this.measure();

      if (!measurements || measurements.totalPages < 2) {
        this.replaceChildren();
        this.totalPages = 0;
        return;
      }

      this.slideOffset = measurements.slideOffset;

      if (this.totalPages !== measurements.totalPages) {
        this.totalPages = measurements.totalPages;
        this.replaceChildren();

        for (let page = 1; page <= this.totalPages; page++) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'slider-counter__link slider-counter__link--dots';
          button.setAttribute('aria-label', String(page));

          const dot = document.createElement('span');
          dot.className = 'dot';
          button.appendChild(dot);

          button.addEventListener('click', () => {
            this.slider.scrollTo({ left: (page - 1) * this.slideOffset });
          });
          this.appendChild(button);
        }
      }

      this.syncActive();
    }

    syncActive() {
      if (!this.totalPages || !this.slideOffset) return;

      const page = Math.min(Math.round(this.slider.scrollLeft / this.slideOffset) + 1, this.totalPages);

      Array.from(this.children).forEach((dot, index) => {
        const isActive = index === page - 1;
        dot.classList.toggle('slider-counter__link--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  if (!customElements.get('kt-slider-dots')) {
    customElements.define('kt-slider-dots', KtSliderDots);
  }
})();
